// src/components/PublicForum.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { askShiaAPI, getStoredToken, getStoredUserId } from '../services/api';

const PublicForum = () => {
  const [activeTab, setActiveTab] = useState('ask');
  const [newQuestion, setNewQuestion] = useState({
    title: '',
    description: '',
    category: 'Personal Growth',
    urgency: 'medium'
  });
  const [myQuestions, setMyQuestions] = useState([]);      // always array
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Load userId & questions on mount
  useEffect(() => {
    const currentUserId = getStoredUserId();
    const hasToken = Boolean(getStoredToken());

    setUserId(hasToken ? currentUserId : null);
    if (currentUserId && hasToken) {
      fetchUserQuestions(currentUserId);
    } else {
      setMyQuestions([]);
    }
  }, []);

  // Fetch user's questions from backend using Ask Shia API
  const fetchUserQuestions = async (userIdParam) => {
    if (!userIdParam || !getStoredToken()) {
      setMyQuestions([]);
      return;
    }

    try {
      setIsLoading(true);
      const { data } = await askShiaAPI.getUserQuestions(userIdParam);

      // make sure it's always an array
      setMyQuestions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch user questions:', error);
      setMyQuestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const forumCategories = [
    { name: 'Personal Growth', icon: '🌱', color: 'from-green-500 to-emerald-500' },
    { name: 'Relationships', icon: '💑', color: 'from-pink-500 to-rose-500' },
    { name: 'Career & Dharma', icon: '💼', color: 'from-blue-500 to-cyan-500' },
    { name: 'Spiritual Practice', icon: '🧘', color: 'from-purple-500 to-indigo-500' },
    { name: 'Family Matters', icon: '👨‍👩‍👧‍👦', color: 'from-orange-500 to-amber-500' },
    { name: 'Ethical Dilemmas', icon: '⚖️', color: 'from-teal-500 to-blue-500' }
  ];

  const validateForm = () => {
    const errors = {};

    if (!newQuestion.title.trim()) {
      errors.title = 'Question title is required';
    } else if (newQuestion.title.length < 5) {
      errors.title = 'Title should be at least 5 characters';
    }

    if (!newQuestion.description.trim()) {
      errors.description = 'Detailed description is required';
    } else if (newQuestion.description.length < 20) {
      errors.description = 'Please provide more details (at least 20 characters)';
    }

    return errors;
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();

    // Always re-check login from localStorage
    const currentUserId = getStoredUserId();
    const currentToken = getStoredToken();
    if (!currentUserId || !currentToken) {
      setUserId(null);
      alert('Please login to ask a question.');
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    setIsLoading(true);

    try {
      // Map frontend urgency to backend priority
      const priorityMap = {
        low: 'LOW',
        medium: 'NORMAL',
        high: 'HIGH'
      };

      const priority = priorityMap[newQuestion.urgency] || 'NORMAL';

      // Submit using Ask Shia API
      await askShiaAPI.submitQuestion(
        currentUserId,
        newQuestion.title,
        newQuestion.description,
        newQuestion.category,
        priority
      );

      alert(
        '✅ Your question has been submitted to Ask Shia! You will receive a comprehensive answer combining scripture references, human wisdom, and AI insights.'
      );

      // Reset form
      setNewQuestion({
        title: '',
        description: '',
        category: 'Personal Growth',
        urgency: 'medium'
      });

      // Refresh user's questions
      setUserId(currentUserId);
      await fetchUserQuestions(currentUserId);
    } catch (error) {
      console.error('Error submitting question:', error);
      alert(`❌ Failed to submit question. Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl mb-6 shadow-2xl">
            <span className="text-2xl">🤖</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent mb-4">
            Ask Shia
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            <span className="text-green-400 font-semibold">S</span>cripture +
            <span className="text-blue-400 font-semibold"> H</span>uman +
            <span className="text-purple-400 font-semibold"> A</span>rtificial Intelligence
          </p>
          <p className="text-lg text-gray-400 mt-2 max-w-3xl mx-auto">
            Submit your complex questions. Our team combines scriptural wisdom with human understanding and AI analysis.
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {/* Single tab - Ask Shia */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-2 border border-gray-700 mb-6">
              <div className="flex justify-center">
                <button
                  onClick={() => setActiveTab('ask')}
                  className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                    activeTab === 'ask'
                      ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white shadow-lg text-lg'
                      : 'text-gray-300'
                  }`}
                >
                  🎯 Ask a Question to Shia
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {activeTab === 'ask' && (
                <div className="space-y-8">
                  {/* Ask Question Form */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700"
                  >
                    <h2 className="text-2xl font-bold text-white mb-6">Submit Your Question</h2>
                    <form onSubmit={handleSubmitQuestion} className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          Question Title *
                        </label>
                        <input
                          type="text"
                          required
                          value={newQuestion.title}
                          onChange={(e) => {
                            setNewQuestion({ ...newQuestion, title: e.target.value });
                            if (formErrors.title) setFormErrors({ ...formErrors, title: null });
                          }}
                          placeholder="Summarize your question in one line..."
                          className={`w-full bg-gray-700/50 border rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/20 transition-all duration-300 ${
                            formErrors.title
                              ? 'border-red-500'
                              : 'border-gray-600 focus:border-green-500'
                          }`}
                        />
                        {formErrors.title && (
                          <p className="text-red-400 text-sm mt-1">{formErrors.title}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          Detailed Description *
                        </label>
                        <textarea
                          required
                          rows={6}
                          value={newQuestion.description}
                          onChange={(e) => {
                            setNewQuestion({ ...newQuestion, description: e.target.value });
                            if (formErrors.description)
                              setFormErrors({ ...formErrors, description: null });
                          }}
                          placeholder="Describe your situation in detail. Include context, challenges, emotions, and what guidance you're seeking..."
                          className={`w-full bg-gray-700/50 border rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/20 transition-all duration-300 resize-vertical ${
                            formErrors.description
                              ? 'border-red-500'
                              : 'border-gray-600 focus:border-green-500'
                          }`}
                        />
                        {formErrors.description && (
                          <p className="text-red-400 text-sm mt-1">{formErrors.description}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-300 mb-2">
                            Category
                          </label>
                          <select
                            value={newQuestion.category}
                            onChange={(e) =>
                              setNewQuestion({ ...newQuestion, category: e.target.value })
                            }
                            className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all duration-300"
                          >
                            {forumCategories.map((cat) => (
                              <option key={cat.name} value={cat.name}>
                                {cat.icon} {cat.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-300 mb-2">
                            Urgency Level
                          </label>
                          <select
                            value={newQuestion.urgency}
                            onChange={(e) =>
                              setNewQuestion({ ...newQuestion, urgency: e.target.value })
                            }
                            className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all duration-300"
                          >
                            <option value="low">✅ I can wait (24-48 hours)</option>
                            <option value="medium">⚠️ Need answer within 24 hours</option>
                            <option value="high">🚨 Urgent - Need answer ASAP</option>
                          </select>
                        </div>
                      </div>
                      
                      
                      <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700"
            >
              <h3 className="text-lg font-bold text-white mb-4">How Shia Works</h3>
              <div className="space-y-4">
                {[
                  { step: '1', icon: '✍️', text: 'You submit your complex life question' },
                  {
                    step: '2',
                    icon: '📖',
                    text: 'Our team finds relevant references from Bhagavad Gita, Upanishads, etc.'
                  },
                  { step: '3', icon: '🤖', text: 'AI synthesizes scripture + human wisdom' },
                  {
                    step: '4',
                    icon: '💫',
                    text: 'You receive a comprehensive, personalized answer'
                  }
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center text-sm font-bold shrink-0">
                      {item.step}
                    </div>
                    <div className="text-sm text-gray-300 flex-1">
                      <div className="text-lg mb-1">{item.icon}</div>
                      {item.text}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-gradient-to-r from-green-500 to-teal-500 text-white py-4 rounded-xl font-semibold text-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {isLoading ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg
                              className="animate-spin h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Submitting to Shia...
                          </span>
                        ) : (
                          'Submit to Ask Shia'
                        )}
                      </button>
                    </form>
                  </motion.div>

                  {/* My Questions & Answers Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700"
                  >
                    <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                      <span className="text-green-400">📬</span> My Questions & Answers
                    </h2>

                    {!userId ? (
                      <div className="text-center py-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-700/50 rounded-full mb-4">
                          <span className="text-2xl">🔒</span>
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2">Login Required</h3>
                        <p className="text-gray-400">
                          Please login to see your questions and answers.
                        </p>
                      </div>
                    ) : isLoading ? (
                      <div className="text-center py-8">
                        <div className="flex items-center justify-center gap-2 text-gray-400">
                          <svg
                            className="animate-spin h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Loading your questions...
                        </div>
                      </div>
                    ) : myQuestions.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-700/50 rounded-full mb-4">
                          <span className="text-2xl">📝</span>
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2">
                          No questions yet
                        </h3>
                        <p className="text-gray-400">
                          Submit your first question above and get a personalized answer from Shia!
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {myQuestions.map((question, index) => (
                          <motion.div
                            key={question.id || index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                            className="bg-gray-700/30 rounded-xl p-6 border border-gray-600 hover:border-green-500/30 transition-colors duration-300"
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-white mb-2">
                                  {question.title}
                                </h3>
                                <div className="flex flex-wrap items-center gap-3">
                                  <span className="px-3 py-1 bg-gray-600/50 text-gray-300 rounded-full text-xs">
                                    {formatDate(question.createdAt)}
                                  </span>
                                  {question.category && (
                                    <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs border border-blue-500/30">
                                      {question.category}
                                    </span>
                                  )}
                                  {question.priority && (
                                    <span
                                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                        question.priority === 'HIGH'
                                          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                          : question.priority === 'MEDIUM'
                                          ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                          : 'bg-green-500/20 text-green-400 border border-green-500/30'
                                      }`}
                                    >
                                      {question.priority}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div
                                className={`px-3 py-1 rounded-full text-xs font-semibold ml-2 ${
                                  question.status === 'ANSWERED'
                                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                    : question.status === 'IN_PROGRESS'
                                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                    : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                }`}
                              >
                                {question.status === 'ANSWERED'
                                  ? '✓ Answered'
                                  : question.status === 'IN_PROGRESS'
                                  ? '🔧 In Progress'
                                  : '⏳ Pending'}
                              </div>
                            </div>

                            {/* Question Body */}
                            {question.body && (
                              <div className="mt-3 mb-4">
                                <p className="text-gray-300 text-sm line-clamp-3">
                                  {question.body}
                                </p>
                              </div>
                            )}

                            {/* Answer Section */}
                            {question.answer ? (
                              <div className="mt-4 p-4 bg-gradient-to-r from-green-900/20 to-teal-900/20 rounded-lg border border-green-500/30">
                                <div className="flex items-center gap-2 mb-3">
                                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center">
                                    <span className="text-sm">🤖</span>
                                  </div>
                                  <h4 className="font-semibold text-green-400">Shia&apos;s Answer:</h4>
                                </div>
                                <div className="text-gray-300 prose prose-invert max-w-none">
                                  {question.answerPreview ? (
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: question.answerPreview
                                      }}
                                    />
                                  ) : (
                                    <p className="whitespace-pre-line">{question.answer}</p>
                                  )}
                                </div>
                                <div className="mt-4 pt-4 border-t border-green-500/20">
                                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                                    <span className="flex items-center gap-1">
                                      <span className="text-green-400">📖</span> Scripture References
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <span className="text-blue-400">👤</span> Human Wisdom
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <span className="text-purple-400">🤖</span> AI Analysis
                                    </span>
                                    {question.answeredAt && (
                                      <span className="ml-auto text-xs">
                                        Answered on: {formatDate(question.answeredAt)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : question.status === 'ANSWERED' && !question.answer ? (
                              <div className="mt-4 p-4 bg-gradient-to-r from-blue-900/20 to-cyan-900/20 rounded-lg border border-blue-500/30">
                                <div className="flex items-center gap-3">
                                  <div className="text-blue-400 text-xl">📨</div>
                                  <div>
                                    <p className="text-blue-300 font-medium">Answer Available</p>
                                    <p className="text-gray-400 text-sm">
                                      Check back soon or refresh the page to see the answer.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-4 p-4 bg-gray-800/50 rounded-lg border border-gray-600">
                                <div className="flex items-center gap-3">
                                  <div className="text-yellow-400 text-xl">⏳</div>
                                  <div>
                                    <p className="text-yellow-300 font-medium">Answer in Progress</p>
                                    <p className="text-gray-400 text-sm">
                                      Our team is working on your answer. You&apos;ll receive a
                                      notification when it&apos;s ready.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                </div>
              )}
            </motion.div>
          </div>


          {/* Sidebar */}
          <div className="lg:w-80 space-y-6">
            {/* How It Works */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700"
            >
              <h3 className="text-lg font-bold text-white mb-4">How Shia Works</h3>
              <div className="space-y-4">
                {[
                  { step: '1', icon: '✍️', text: 'You submit your complex life question' },
                  {
                    step: '2',
                    icon: '📖',
                    text: 'Our team finds relevant references from Bhagavad Gita, Upanishads, etc.'
                  },
                  { step: '3', icon: '🤖', text: 'AI synthesizes scripture + human wisdom' },
                  {
                    step: '4',
                    icon: '💫',
                    text: 'You receive a comprehensive, personalized answer'
                  }
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center text-sm font-bold shrink-0">
                      {item.step}
                    </div>
                    <div className="text-sm text-gray-300 flex-1">
                      <div className="text-lg mb-1">{item.icon}</div>
                      {item.text}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Categories at a Glance */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl p-6 border border-blue-500/20"
            >
              <h3 className="text-lg font-bold text-white mb-4">Common Question Categories</h3>
              <div className="grid grid-cols-2 gap-3">
                {forumCategories.map((category) => (
                  <div
                    key={category.name}
                    className="bg-gray-800/40 rounded-xl p-3 text-center hover:bg-gray-700/60 transition-colors duration-300 cursor-pointer hover:scale-105 transform"
                    onClick={() =>
                      setNewQuestion({ ...newQuestion, category: category.name })
                    }
                  >
                    <div className="text-2xl mb-1">{category.icon}</div>
                    <div className="text-xs text-gray-300">{category.name}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Quick Tips */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-6 border border-purple-500/20"
            >
              <h3 className="text-lg font-bold text-white mb-3">Tips for Better Answers</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Be specific about your situation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Include relevant context and challenges</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Mention what you&apos;ve tried already</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Be open to scriptural guidance</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicForum;
